import React, { useCallback, useEffect, useMemo, useState } from 'react'
import posthog from 'posthog-js'
import i18n from '@renderer/i18n'
import {
  getHostAppVersion,
  getPreloadApi,
  getSubscriptionState,
  hasElectronPreload
} from '@renderer/lib/runtime'
import {
  KEPT_AS_SECOND_BOOTH_KEY,
  NO_DUPLICATE,
  detectDeadProfileWindow,
  detectDuplicateLaunch,
  lastLogTimestamp,
  primaryLogFileFor,
  readProfileConfigured,
  sqliteRemembersSetup,
  type DuplicateLaunchVerdict
} from '@renderer/utils/duplicateLaunch'
import { guardLanguage } from '@renderer/utils/guardLanguage'

/** The whole check is bounded: past this the booth boots as it always has. */
const CHECK_BUDGET_MS = 2000
/** An untouched booth2 cannot serve a guest, so it closes itself if nobody answers. */
const AUTO_CLOSE_SECONDS = 15
const CLOSE_FALLBACK_MS = 1500

function readKeptAsSecondBooth(): boolean {
  try {
    return localStorage.getItem(KEPT_AS_SECOND_BOOTH_KEY) === '1'
  } catch {
    return false
  }
}

async function gatherAndDetect(): Promise<DuplicateLaunchVerdict> {
  const api = getPreloadApi()
  const [shellVersion, logDirectory] = await Promise.all([
    getHostAppVersion(),
    typeof api?.logs?.getDirectory === 'function'
      ? api.logs.getDirectory().catch(() => null)
      : Promise.resolve(null)
  ])
  let profileConfigured = true
  try {
    profileConfigured = readProfileConfigured(localStorage)
  } catch {
    // Unreadable storage: assume set up, so a real booth is never held back.
  }
  const secondProfile = await detectDuplicateLaunch(
    {
      shellVersion,
      logDirectory,
      profileConfigured,
      keptAsSecondBooth: readKeptAsSecondBooth()
    },
    (profile) => readPrimaryLastLogAt(profile)
  )
  if (secondProfile.kind !== 'none') return secondProfile
  return detectDeadProfileWindow({ shellVersion, profileConfigured }, readMainRemembersSetup)
}

/**
 * Whether this PC's SQLite remembers the booth being used. Main reads that file
 * on its own, so it answers even in a window whose Chromium storage is dead.
 */
async function readMainRemembersSetup(): Promise<boolean> {
  const subscription = await getSubscriptionState()
  if (sqliteRemembersSetup(subscription, 0)) return true
  const listSessions = getPreloadApi()?.reprint?.listSessions
  if (typeof listSessions !== 'function') return false
  try {
    const result = await listSessions()
    return sqliteRemembersSetup(null, result?.success ? (result.sessions?.length ?? 0) : 0)
  } catch {
    return false
  }
}

/**
 * When the primary booth last wrote its log. `logs:read-file` on these shells reads
 * any path; should that ever be narrowed to the window's own log folder, this
 * reads null and booth2 simply boots as it always did.
 */
async function readPrimaryLastLogAt(profile: string): Promise<number | null> {
  const logs = getPreloadApi()?.logs
  if (typeof logs?.getTodayFile !== 'function' || typeof logs?.readFile !== 'function') {
    return null
  }
  try {
    const primaryFile = primaryLogFileFor(await logs.getTodayFile(), profile)
    return primaryFile ? lastLogTimestamp(await logs.readFile(primaryFile)) : null
  } catch {
    return null
  }
}

function withDeadline<T>(work: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([
    work.catch(() => fallback),
    new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms))
  ])
}

/**
 * Holds the booth back until it is known not to be a window opened by starting
 * the app twice (see utils/duplicateLaunch.ts): a blank booth2 on shells
 * v2.4.12–v2.10.4, or a window that cannot read its own profile on shells older
 * than v2.4.10. Everything else renders the booth as soon as the shell has answered.
 */
const DuplicateLaunchGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [verdict, setVerdict] = useState<DuplicateLaunchVerdict | null>(() =>
    hasElectronPreload() ? null : NO_DUPLICATE
  )

  useEffect(() => {
    if (verdict) return
    let cancelled = false
    void withDeadline(gatherAndDetect(), CHECK_BUDGET_MS, NO_DUPLICATE).then((result) => {
      if (cancelled) return
      if (result.kind !== 'none') {
        console.warn('[DUPLICATE-LAUNCH] Holding the booth back:', result)
        posthog.capture('duplicate_launch_guard_shown', {
          kind: result.kind,
          profile: result.kind === 'blank-second-profile' ? result.profile : undefined
        })
      }
      setVerdict(result)
    })
    return (): void => {
      cancelled = true
    }
    // Decided once per page load.
  }, [])

  if (!verdict) return null
  if (verdict.kind === 'none') return <>{children}</>
  if (verdict.kind === 'dead-profile') {
    return <DeadProfileScreen onContinue={(): void => setVerdict(NO_DUPLICATE)} />
  }
  return <DuplicateLaunchScreen onKeep={(): void => setVerdict(NO_DUPLICATE)} />
}

/** Close this window; if Chromium refuses, say how to do it by hand. */
function useCloseWindow(event: string): { close: () => void; closeFailed: boolean } {
  const [closeFailed, setCloseFailed] = useState(false)
  const close = useCallback((): void => {
    posthog.capture(event)
    window.close()
    // Closing the last window quits the app on every shell. If Chromium refused
    // the close, say how to do it by hand rather than leave a dead button.
    setTimeout(() => setCloseFailed(true), CLOSE_FALLBACK_MS)
  }, [event])
  return { close, closeFailed }
}

/**
 * A pre-2.4.10 window that cannot read this PC's profile. Unlike booth2 there is no
 * log to prove the running booth is alive, so it never closes on its own; the
 * operator closes it, or continues here if no other window exists.
 */
const DeadProfileScreen: React.FC<{ onContinue: () => void }> = ({ onContinue }) => {
  const t = useMemo(() => i18n.getFixedT(guardLanguage()), [])
  const { close, closeFailed } = useCloseWindow('duplicate_launch_dead_profile_closed')

  const continueHere = (): void => {
    posthog.capture('duplicate_launch_dead_profile_continued')
    onContinue()
  }

  return (
    <div className="bg-whiteout flex h-screen items-center justify-center p-6">
      <div className="w-full max-w-lg p-8 bg-white rounded-lg shadow-2xl text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-3">{t('duplicateLaunch.deadTitle')}</h1>
        <p className="text-gray-600 mb-6">{t('duplicateLaunch.deadBody')}</p>
        <button
          onClick={close}
          className="w-full py-3 px-6 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 focus:ring-4 focus:ring-purple-300 transition duration-300"
        >
          {t('duplicateLaunch.closeNow')}
        </button>
        {closeFailed && (
          <p className="text-sm text-gray-700 mt-3">{t('duplicateLaunch.closeByHand')}</p>
        )}
        <p className="text-sm text-gray-600 mt-6">{t('duplicateLaunch.deadNoOtherWindow')}</p>
        <button
          onClick={continueHere}
          className="mt-4 min-h-[44px] text-sm text-gray-500 underline hover:text-gray-700"
        >
          {t('duplicateLaunch.deadContinue')}
        </button>
      </div>
    </div>
  )
}

const DuplicateLaunchScreen: React.FC<{ onKeep: () => void }> = ({ onKeep }) => {
  const t = useMemo(() => i18n.getFixedT(guardLanguage()), [])
  const [secondsLeft, setSecondsLeft] = useState(AUTO_CLOSE_SECONDS)
  const { close, closeFailed } = useCloseWindow('duplicate_launch_guard_closed')

  useEffect(() => {
    if (secondsLeft <= 0) {
      close()
      return
    }
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000)
    return (): void => clearTimeout(timer)
  }, [secondsLeft, close])

  const keepAsSecondBooth = (): void => {
    try {
      localStorage.setItem(KEPT_AS_SECOND_BOOTH_KEY, '1')
    } catch {
      // still open the booth for this run
    }
    posthog.capture('duplicate_launch_guard_kept')
    onKeep()
  }

  return (
    <div className="bg-whiteout flex h-screen items-center justify-center p-6">
      <div className="w-full max-w-lg p-8 bg-white rounded-lg shadow-2xl text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-3">{t('duplicateLaunch.title')}</h1>
        <p className="text-gray-600 mb-6">{t('duplicateLaunch.secondBooth')}</p>
        <button
          onClick={close}
          className="w-full py-3 px-6 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-600 focus:ring-4 focus:ring-purple-300 transition duration-300"
        >
          {t('duplicateLaunch.closeIn', { seconds: Math.max(secondsLeft, 0) })}
        </button>
        {closeFailed && (
          <p className="text-sm text-gray-700 mt-3">{t('duplicateLaunch.closeByHand')}</p>
        )}
        <button
          onClick={keepAsSecondBooth}
          className="mt-4 text-sm text-gray-500 underline hover:text-gray-700"
        >
          {t('duplicateLaunch.keepSecondBooth')}
        </button>
        <p className="text-xs text-gray-400 mt-6">{t('duplicateLaunch.noOtherWindow')}</p>
      </div>
    </div>
  )
}

export default DuplicateLaunchGate
